import * as providerConfigs from './configs';
import { useSecondLookIntegration } from '../../queries';

/**
 * Safely maps a second-look provider name to its customer-facing display name.
 *
 * Uses a direct lookup against the registered provider configs rather than going through
 * `useProviderConfig`, which throws for unrecognised providers. A future provider that
 * appears in tenant settings before a `ProviderConfig` entry exists will degrade
 * gracefully: the raw provider name is returned instead of crashing.
 */
const getSecondLookDisplayName = (providerName: string): string => {
    const matchingKey = (Object.keys(providerConfigs) as (keyof typeof providerConfigs)[]).find(
        key => key.toLowerCase() === providerName.toLowerCase(),
    );
    return (
        (matchingKey ? providerConfigs[matchingKey]?.providerDisplayName : undefined) ??
        providerName
    );
};

/**
 * Resolves the customer-facing display name for the tenant's configured second-look provider.
 *
 * The name is read from the resolved second-look provider's {@link ProviderConfig.providerDisplayName},
 * falling back to the raw provider name and finally an empty string. Use this for any second-look
 * copy (declined screens, service-degraded alerts, CTAs) so the shared code path stays
 * provider-agnostic.
 */
export const useSecondLookProviderName = (): string => {
    const { secondLookProvider } = useSecondLookIntegration();
    if (!secondLookProvider) {
        return '';
    }
    return getSecondLookDisplayName(secondLookProvider);
};
